import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { resolveDetailId } from "@/utils/detailNav";
import {
  getHotelDetail,
  getHotelPrice,
  getHotelRooms,
  preCreateHotelOrder,
  type HotelDetail,
  type HotelRoomItem2,
} from "@/api/hotel";
import DateTimeRangePicker from "@/components/DateTimeRangePicker";
import ImageCarousel from "@/components/ImageCarousel";
import { useHotelBookingStore } from "@/store/hotelBookingStore";
import { toastError } from "@/utils/toast";

import HotelInfo from "./components/HotelInfo";
import RoomList from "./components/RoomList";
import Facilities from "./components/Facilities";
import Reviews from "./components/Reviews";
import HotelIntro from "./components/HotelIntro";
import BottomBar from "./components/BottomBar";

/** 将 API 返回的评论转换为组件格式 */
function mapComments(data: HotelDetail["comment_list"]) {
  return data.map((c) => ({
    id: c.id,
    avatar: c.avatar,
    name: c.nickname,
    rating: Math.round(c.product_score / 20) || 5,
    date: c.add_time_text || "",
    content: c.comment,
  }));
}

/** 将 API 返回的房型转换为组件格式 */
function mapRooms(data: HotelDetail["rooms"]) {
  return data.map((r) => ({
    id: r.id,
    name: r.room_name,
    bed: r.bed_type_text,
    area: `${r.area}㎡`,
    breakfast: r.breakfast_text,
    available: r.stock,
    price: r.price,
  }));
}

/** 将 getRooms 接口返回的房型转换为组件格式 */
function mapRooms2(data: HotelRoomItem2[]) {
  return data.map((r) => ({
    id: r.id,
    name: r.room_name,
    bed: r.bed_type_text,
    area: `${r.area}㎡`,
    breakfast: r.breakfast_text,
    available: r.stock ?? 0,
    price: r.price,
  }));
}

/** MM-DD 格式转 YYYY-MM-DD（补当年份） */
function toFullDate(md: string): string {
  if (!md) return "";
  const year = new Date().getFullYear();
  return `${year}-${md}`;
}

/** YYYY-MM-DD 或 YYYY-MM-DD HH:mm 格式转 MM-DD */
function toDateMD(ymd: string): string {
  if (!ymd) return "";
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+\d{2}:\d{2})?/);
  return m ? `${m[2]}-${m[3]}` : "";
}

/** YYYY-MM-DD 格式 */
function fmtDate(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** 默认入住日期：今天 14:00 入住，明天 12:00 离店（1晚） */
function defaultDates(): { checkIn: string; checkOut: string } {
  const today = new Date();
  const tomorrow = new Date(today.getTime() + 86400000);
  return {
    checkIn: `${fmtDate(today)} 14:00`,
    checkOut: `${fmtDate(tomorrow)} 12:00`,
  };
}

/** 判断日期是否已过期（早于今天）。支持 "YYYY-MM-DD HH:mm" 或 "YYYY-MM-DD"。 */
function isPastDate(ymd: string): boolean {
  if (!ymd) return false;
  const datePart = ymd.split(" ")[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return false;
  // YYYY-MM-DD 字符串可直接按字典序比较
  return datePart < fmtDate(new Date());
}

export default function HotelDetailProductPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ★ 同时支持 URL query（?hotelId=x，刷新不丢）与 location.state（统一走工具函数）
  const hotelId = resolveDetailId("hotelId", searchParams, location.state);

  // 从 store 恢复当前酒店之前选过的日期（返回时保持，不回退默认值）
  const savedHotelId = useHotelBookingStore((s) => s.hotelId);
  const savedCheckIn = useHotelBookingStore((s) => s.checkIn);
  const savedCheckOut = useHotelBookingStore((s) => s.checkOut);
  const saveDates = useHotelBookingStore((s) => s.saveDates);

  const [hotel, setHotel] = useState<HotelDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkIn, setCheckIn] = useState(
    savedHotelId === hotelId && savedCheckIn && !isPastDate(savedCheckIn)
      ? savedCheckIn
      : defaultDates().checkIn,
  );
  const [checkOut, setCheckOut] = useState(
    savedHotelId === hotelId && savedCheckOut && !isPastDate(savedCheckOut)
      ? savedCheckOut
      : defaultDates().checkOut,
  );
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [rooms, setRooms] = useState<HotelRoomItem2[] | null>(null);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [totalPriceFromApi, setTotalPriceFromApi] = useState<number | null>(
    null,
  );
  // 查价请求中：选中房型后到 getPrice 返回前为 true，用于置灰预订按钮
  const [priceLoading, setPriceLoading] = useState(false);
  // 查价失败信息：非空表示价格异常/查询失败，用于置灰预订按钮并提示
  const [priceError, setPriceError] = useState<string | null>(null);
  // ── 后端限流状态：testmex 环境 /home/hotel/* 接口存在约 30 秒滚动窗口限流，
  //    触发后（getPrice/pre-create 返回 444）在窗口内所有请求都会失败。
  //    一旦命中，置 true 并禁用预订 30 秒，等窗口重置后再允许操作。
  //    注意：code=400 是业务错误（如"价格异常"），不触发限流冷却。 ──
  const [rateLimited, setRateLimited] = useState(false);
  const rateLimitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 触发限流冷却：命中后端 444/400 后置 rateLimited=true，30 秒后自动复位。
  // 期间禁用预订按钮，避免用户在限流窗口内反复撞墙。
  // onCooldownEnd：冷却结束时的回调（用于自动重查 getPrice，让按钮真正可恢复）。
  const enterRateLimited = useCallback((onCooldownEnd?: () => void) => {
    setRateLimited(true);
    if (rateLimitTimerRef.current) clearTimeout(rateLimitTimerRef.current);
    rateLimitTimerRef.current = setTimeout(() => {
      setRateLimited(false);
      onCooldownEnd?.();
    }, 30000);
  }, []);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (rateLimitTimerRef.current) clearTimeout(rateLimitTimerRef.current);
    };
  }, []);

  // 获取酒店详情
  useEffect(() => {
    const fetchDetail = async () => {
      if (!hotelId) return;
      try {
        setLoading(true);
        const res = await getHotelDetail(hotelId);
        if (res.data.code === 200 && res.data.data) {
          setHotel(res.data.data);
        }
      } catch (e) {
        console.error("[酒店详情] 获取失败:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [hotelId]);

  // 选择日期后按日期拉取房型（含当日价格/库存）
  useEffect(() => {
    if (!hotelId || !checkIn || !checkOut) return;
    let cancelled = false;
    setRoomsLoading(true);
    // 日期变化后立即清空已选房型，避免"旧房型 + 新日期"发起无意义的查价
    setSelectedRoomId(null);
    getHotelRooms({
      hotel_id: hotelId,
      start_date: toFullDate(toDateMD(checkIn)),
      end_date: toFullDate(toDateMD(checkOut)),
    })
      .then((res) => {
        if (cancelled) return;
        if (res.data.code === 200 && Array.isArray(res.data.data)) {
          setRooms(res.data.data);
          setSelectedRoomId(null);
        }
      })
      .catch((e) => {
        // 失败时清空 rooms，让 displayRoomSource 回退到详情自带房型，
        // 避免残留上一个日期的旧房型与旧价格
        if (!cancelled) setRooms(null);
        console.error("[酒店详情] 按日期获取房型失败:", e);
      })
      .finally(() => {
        if (!cancelled) setRoomsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn, checkOut]);

  // 选完房型 + 日期后，按房型+日期查询真实总价。
  // ★ 查价期间（priceLoading）置灰预订按钮；查价成功（拿到真实价）才放开。
  //   查价失败（价格异常/网络异常）则置灰按钮并 toast 提示，不允许下单。
  useEffect(() => {
    if (!hotelId || !checkIn || !checkOut || !selectedRoomId) {
      setTotalPriceFromApi(null);
      setPriceLoading(false);
      setPriceError(null);
      return;
    }
    // 限流冷却中：跳过自动查价，避免在限流窗口内白费请求并重置冷却计时。
    // 等冷却结束（rateLimited 变 false）effect 会因依赖变化再次触发并真正查价。
    if (rateLimited) {
      setPriceLoading(false);
      return;
    }
    let cancelled = false;
    // 开始查价：清空上一次真实价与失败信息，并置灰预订按钮
    setTotalPriceFromApi(null);
    setPriceError(null);
    setPriceLoading(true);
    const run = async () => {
      try {
        for (let attempt = 0; attempt < 2; attempt++) {
          if (cancelled) return;
          try {
            const res = await getHotelPrice({
              hotel_id: hotelId,
              start_date: toFullDate(toDateMD(checkIn)),
              end_date: toFullDate(toDateMD(checkOut)),
              room_id: selectedRoomId,
            });
            if (cancelled) return;
            if (res.data.code === 200 && res.data.data) {
              setTotalPriceFromApi(res.data.data.total_price);
              setPriceError(null);
              return;
            }
            // ★ 后端限流：code=444 表示 30 秒滚动窗口限流，命中后进入冷却，
            //   冷却结束自动重查一次。限流窗口内重试无意义，不要走下面的静默重试。
            if (res.data.code === 444) {
              if (cancelled) return;
              console.warn("[酒店详情] getPrice 命中后端限流(444)");
              const msg = "系统繁忙，请稍后重试";
              setPriceError(msg);
              toastError(msg);
              // 进入冷却：rateLimited 置 true 后，effect 会因依赖变化重新执行并跳过查价；
              // 冷却结束 rateLimited 置 false 时，effect 再次执行并自动重查当前房型价格。
              enterRateLimited();
              return;
            }
            // 业务失败（如价格异常）：静默重试一次，仍失败则置灰并提示
            if (attempt === 0) {
              await new Promise((r) => setTimeout(r, 1000));
              continue;
            }
            const bizMsg =
              res.data.message || "该房型价格异常，请选择其他房型或日期";
            console.warn(
              `[酒店详情] getPrice 业务失败(code=${res.data.code})`,
              bizMsg,
            );
            // ★ HTTP 200 但业务繁忙（后端返回"请求繁忙，请稍后重试"等提示）：
            //   只重新请求查价接口（刷新底部价格），不刷新整个详情页。
            //   最多自动重查 5 次（间隔 1.5s），成功即更新底部价格；仍失败则置灰提示。
            if (/繁忙|请稍后重试|系统繁忙/.test(bizMsg)) {
              for (let retry = 0; retry < 5; retry++) {
                if (cancelled) return;
                await new Promise((r) => setTimeout(r, 1500));
                try {
                  const retryRes = await getHotelPrice({
                    hotel_id: hotelId,
                    start_date: toFullDate(toDateMD(checkIn)),
                    end_date: toFullDate(toDateMD(checkOut)),
                    room_id: selectedRoomId,
                  });
                  if (cancelled) return;
                  if (retryRes.data.code === 200 && retryRes.data.data) {
                    setTotalPriceFromApi(retryRes.data.data.total_price);
                    setPriceError(null);
                    return;
                  }
                  if (retryRes.data.code === 444) {
                    if (cancelled) return;
                    console.warn("[酒店详情] getPrice 重查命中后端限流(444)");
                    setPriceError("系统繁忙，请稍后重试");
                    toastError("系统繁忙，请稍后重试");
                    enterRateLimited();
                    return;
                  }
                  const retryMsg =
                    retryRes.data.message || "该房型价格异常，请选择其他房型或日期";
                  if (!/繁忙|请稍后重试|系统繁忙/.test(retryMsg)) {
                    // 不再是繁忙类错误（如价格异常）：直接透传并置灰
                    setPriceError(retryMsg);
                    toastError(retryMsg);
                    return;
                  }
                  console.warn(
                    `[酒店详情] getPrice 第 ${retry + 1} 次重查仍繁忙:`,
                    retryMsg,
                  );
                } catch (e) {
                  console.warn(
                    `[酒店详情] getPrice 第 ${retry + 1} 次重查网络异常`,
                    e,
                  );
                }
              }
              const busyMsg = "查询繁忙，请稍等";
              setPriceError(busyMsg);
              toastError(busyMsg);
              return;
            }
            setPriceError(bizMsg);
            toastError(bizMsg);
            return;
          } catch (e) {
            console.warn(`[酒店详情] getPrice 第 ${attempt + 1} 次网络异常`, e);
            if (attempt === 0) {
              await new Promise((r) => setTimeout(r, 1000));
              continue;
            }
            const netMsg = "价格查询失败，请稍后重试";
            setPriceError(netMsg);
            toastError(netMsg);
            return;
          }
        }
      } finally {
        if (!cancelled) setPriceLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIn, checkOut, selectedRoomId, rateLimited]);

  // loading 状态
  if (loading) {
    return (
      <div
        className="min-h-screen"
        style={{
          background: "#F8F9FC",
          fontFamily: "'Inter','SF Pro Text',sans-serif",
        }}
      >
        {/* 图片骨架 */}
        <div
          className="animate-pulse"
          style={{ height: 260, background: "#E5E7EB" }}
        />
        {/* 内容骨架 */}
        <div className="bg-white px-5 py-6 space-y-4">
          <div
            className="h-7 animate-pulse rounded"
            style={{ width: "60%", background: "#E5E7EB" }}
          />
          <div
            className="h-4 animate-pulse rounded"
            style={{ width: "80%", background: "#F0F0F0" }}
          />
          <div
            className="h-4 animate-pulse rounded"
            style={{ width: "40%", background: "#F0F0F0" }}
          />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white mt-2 px-5 py-4 space-y-3">
            <div
              className="h-5 animate-pulse rounded"
              style={{ width: "30%", background: "#E5E7EB" }}
            />
            <div
              className="h-32 animate-pulse rounded-xl"
              style={{ background: "#F8F9FC" }}
            />
          </div>
        ))}
      </div>
    );
  }

  // 无数据
  if (!hotel) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background: "#F8F9FC",
          fontFamily: "'Inter','SF Pro Text',sans-serif",
        }}
      >
        <div className="text-center">
          <i className="ri-hotel-line text-5xl text-gray-300" />
          <p className="text-sm text-gray-400 mt-3">暂无酒店信息</p>
        </div>
      </div>
    );
  }

  // 根据日期计算入住晚数
  const calcNights = (inDate: string, outDate: string): number => {
    if (!inDate || !outDate) return 0;
    const diff = new Date(outDate).getTime() - new Date(inDate).getTime();
    return Math.max(1, Math.ceil(diff / 86400000));
  };
  const nights = calcNights(checkIn, checkOut);

  // 当前显示的房型数据源（getRooms 优先，否则用详情里的房型）
  const displayRoomSource =
    rooms && rooms.length > 0 ? rooms : (hotel.rooms as unknown as HotelRoomItem2[]);

  // 只有选择了房型和日期时才显示价格
  const selectedRoom = selectedRoomId
    ? displayRoomSource.find((r) => r.id === selectedRoomId) || null
    : null;
  const localPrice =
    selectedRoom && nights > 0 ? selectedRoom.price * nights : 0;
  // 优先使用接口返回的真实总价；未返回时回退到本地估算
  const totalPrice = totalPriceFromApi ?? localPrice;

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+112px)]"
      style={{
        background: "#F8F9FC",
        fontFamily: "'Inter','SF Pro Text',sans-serif",
      }}
    >
      {/* 图片轮播（公共组件：自动播放 + 点击全屏预览 + 主区计数 + 左上角返回） */}
      <ImageCarousel
        images={hotel.images.length > 0 ? hotel.images : [hotel.cover_image]}
        height={260}
        autoplayDelay={4000}
        name={hotel.hotel_name}
        showBack
        showCounter
      />

      {/* 酒店基本信息 */}
      <HotelInfo
        starLevel={hotel.star_rating_text}
        name={hotel.hotel_name}
        address={hotel.address || hotel.full_address}
        rating={Number(
          (
            hotel.comment_stats?.avg_product_score ||
            hotel.avg_product_score ||
            0
          ).toFixed(1),
        )}
        reviewCount={hotel.comment_stats?.comment_count || hotel.comment_count}
        checkInTime="14:00"
        checkOutTime="12:00"
      />

      {/* 入住日期选择 */}
      <div className="bg-white mt-2 px-5 py-4">
        <div className="text-sm font-semibold text-gray-800 mb-3">入住日期</div>
        <button
          onClick={() => setShowDatePicker(true)}
          className="w-full flex items-center justify-between rounded-2xl px-4 py-3 cursor-pointer"
          style={{ background: "#F8F9FC", border: "1.5px solid #E5E7EB" }}
        >
          <div className="flex items-center gap-3">
            <div className="text-center">
              <div className="text-xs text-gray-400">入住</div>
              <div
                className={`text-sm font-bold ${!checkIn ? "text-gray-300" : "text-gray-800"}`}
              >
                {checkIn ? checkIn.split(" ")[0] : "请选择"}
              </div>
              {checkIn && (
                <div className="text-xs mt-0.5" style={{ color: "#F97316" }}>
                  {checkIn.split(" ")[1]}
                </div>
              )}
            </div>
            <div className="flex flex-col items-center gap-0.5">
              <div className="text-xs text-gray-400">
                {nights > 0 ? `${nights}晚` : "-"}
              </div>
              <div className="flex items-center gap-1">
                <div className="h-px w-10" style={{ background: "#CBD5E1" }} />
                <i
                  className="ri-moon-line"
                  style={{ color: "#9CA3AF", fontSize: 12 }}
                />
                <div className="h-px w-10" style={{ background: "#CBD5E1" }} />
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-400">离店</div>
              <div
                className={`text-sm font-bold ${!checkOut ? "text-gray-300" : "text-gray-800"}`}
              >
                {checkOut ? checkOut.split(" ")[0] : "请选择"}
              </div>
              {checkOut && (
                <div className="text-xs mt-0.5" style={{ color: "#F97316" }}>
                  {checkOut.split(" ")[1]}
                </div>
              )}
            </div>
          </div>
          <i
            className="ri-pencil-line"
            style={{ color: "#1E3A8A", fontSize: 16 }}
          />
        </button>
      </div>

      {/* 日期范围选择器（公共组件） */}
      <DateTimeRangePicker
        visible={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        title="选择入住日期"
        startLabel="入住时间"
        endLabel="离店时间"
        initialRange={
          checkIn && checkOut
            ? {
                startDate: toDateMD(checkIn),
                startTime: "14:00",
                endDate: toDateMD(checkOut),
                endTime: "12:00",
              }
            : undefined
        }
        onConfirm={(range) => {
          const nextCheckIn = `${toFullDate(range.startDate)} ${range.startTime}`;
          const nextCheckOut = `${toFullDate(range.endDate)} ${range.endTime}`;
          setCheckIn(nextCheckIn);
          setCheckOut(nextCheckOut);
          saveDates(hotelId, nextCheckIn, nextCheckOut);
        }}
      />

      {/* 房型列表 */}
      <RoomList
        rooms={
          rooms && rooms.length > 0
            ? mapRooms2(rooms)
            : mapRooms(hotel.rooms)
        }
        loading={roomsLoading}
        selectedRoomId={selectedRoomId}
        onRoomSelect={(id) =>
          setSelectedRoomId(id === selectedRoomId ? null : id)
        }
      />

      {/* 酒店设施 */}
      <Facilities facilities={hotel.facilities} />

      {/* 用户评价 */}
      <Reviews
        reviews={mapComments(hotel.comment_list)}
        reviewCount={hotel.comment_stats?.comment_count || 0}
      />

      {/* 酒店介绍 */}
      <HotelIntro intro={hotel.hotel_intro} />

      {/* 底部预订栏 */}
      <BottomBar
        nights={nights}
        totalPrice={totalPrice}
        hasSelection={!!selectedRoom && nights > 0}
        priceError={priceError}
        // ★ disabled 由"是否选了房型/日期"、"查价失败(priceError)"、"pre-create 冷却中"共同控制。
        //   ★ 查价中(priceLoading)不并入 disabled——否则按钮会变灰，看不到 spinner；
        //     仅依赖 BottomBar 内部用 loading 关闭 onClick + cursor='wait' 让用户感知等待。
        //   只有"业务真的不能点"（缺房型/日期、查价失败、限流）才进 disabled 显示灰底。
        disabled={
          !selectedRoom ||
          !checkIn ||
          !checkOut ||
          rateLimited ||
          !!priceError
        }
        // ★ 拆成 priceLoading / bookingLoading 两个独立状态，让 BottomBar 精确区分：
        //   - 查价中（priceLoading）：价格区显示 spinner + "价格查询中..."，按钮 spinner + "请稍候"。
        //   - 下单中（bookingLoading）：价格区保持已查到的真实价（priceLoading=false），仅按钮 spinner。
        //   两个阶段互斥（pre-create 只能在查价成功后才允许点），不会同时为 true。
        priceLoading={priceLoading}
        bookingLoading={bookingLoading}
        roomName={selectedRoom?.room_name}
        onBook={async () => {
          // 防御性校验：查价中或查价失败时不允许下单（按钮 disabled 已兜底，这里再拦一道）
          if (
            !selectedRoom ||
            !checkIn ||
            !checkOut ||
            bookingLoading ||
            priceLoading ||
            !!priceError
          )
            return;

          // 拦截 price <= 0 的兜底
          const roomPrice = (selectedRoom as { price?: number } | undefined)?.price;
          if (!roomPrice || roomPrice <= 0) {
            toastError("该房型暂无可售价格，请选择其他房型");
            return;
          }

          // 跳转前记住当前日期，返回时保持
          saveDates(hotelId, checkIn, checkOut);
          setBookingLoading(true);
          try {
            // ★ 接口文档要求 check_in_date / check_out_date 为 YYYY-MM-DD：
            //   本地 checkIn 形如 "2026-08-17 14:00"，必须先 split 取日期段，
            //   否则后端校验会报 400 "价格异常"（时分被带入会破坏价格分段计算）
            const payload = {
              room_id: selectedRoom.id,
              check_in_date: toFullDate(toDateMD(checkIn)),
              check_out_date: toFullDate(toDateMD(checkOut)),
              hotel_id: hotelId,
            };
            console.log("[酒店详情] pre-create 请求参数:", payload);

            // ── pre-create：仅 code=444 表示后端 30 秒滚动限流。
            //    命中 444 时快速重试 1 次（约 1 秒后），仍失败则进入冷却（禁用预订 30 秒）。
            //    注意：code=400 是业务错误（如"价格异常"），不是限流，应直接透传后端 message。 ──
            let result: Awaited<ReturnType<typeof preCreateHotelOrder>> | null = null;
            let rateLimitedHit = false;
            for (let attempt = 0; attempt < 2; attempt++) {
              try {
                result = await preCreateHotelOrder(payload);
                if (result.data.code === 200 && result.data.data) break;
                if (result.data.code === 444) {
                  rateLimitedHit = true;
                  console.warn(
                    `[酒店详情] pre-create 命中后端限流(code=${result.data.code})，第 ${attempt + 1} 次${
                      attempt === 0 ? "，1000ms 后重试" : ""
                    }`,
                  );
                  if (attempt === 0) {
                    await new Promise((r) => setTimeout(r, 1000));
                    continue;
                  }
                  break;
                }
                // 其他业务错误（400 价格异常等）：直接中断，透传 message
                break;
              } catch (err) {
                // 网络异常：重试 1 次
                if (attempt === 0) {
                  console.warn(
                    "[酒店详情] pre-create 网络异常，1000ms 后重试:",
                    err,
                  );
                  await new Promise((r) => setTimeout(r, 1000));
                  continue;
                }
                throw err;
              }
            }

            if (result!.data.code === 200 && result!.data.data) {
              navigate("/order-form", {
                state: {
                  type: "hotel",
                  orderId: result!.data.data.order_id,
                  roomId: selectedRoom.id,
                },
              });
            } else {
              // 命中限流 → 进入冷却 30 秒；否则为其他业务错误
              if (rateLimitedHit) {
                enterRateLimited();
                console.warn(
                  "[酒店详情] pre-create 连续命中限流，进入 30 秒冷却",
                  result!.data.code,
                  result!.data.message,
                );
                toastError("系统繁忙，请 30 秒后重试");
              } else {
                console.warn(
                  "[酒店详情] 预创建订单失败:",
                  result!.data.code,
                  result!.data.message,
                  "完整响应:",
                  JSON.stringify(result!.data),
                );
                toastError(
                  result!.data.message
                    ? `${result!.data.message}，请更换其他房型或日期后重试`
                    : "订单创建失败，请稍后重试",
                );
              }
            }
          } catch (err) {
            // 网络/服务异常
            console.error("预创建酒店订单失败:", err);
            toastError("订单创建失败，请稍后重试");
          } finally {
            setBookingLoading(false);
          }
        }}
      />
    </div>
  );
}
